/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useEffect, useRef } from 'react'

import { cx } from '../utils'

export interface CheckboxProps {
  id: string
  label: React.ReactNode
  checked: boolean
  /** A partial selection (some children checked); rendered as the mixed state. */
  indeterminate?: boolean
  disabled?: boolean
  onChange: (checked: boolean) => void
  size?: 'xs' | 'sm' | 'md'
  className?: string
  'data-testid'?: string
  /** Badges and other decorations rendered after the label. */
  trailing?: React.ReactNode
}

const sizeClass: Record<NonNullable<CheckboxProps['size']>, string> = {
  xs: 'checkbox-xs',
  sm: 'checkbox-sm',
  md: '',
}

/**
 * A daisyUI checkbox with its label as a sibling, not a parent — a nested label
 * re-dispatches the click onto the control and double-toggles it.
 */
export const Checkbox: React.FC<CheckboxProps> = ({
  id,
  label,
  checked,
  indeterminate = false,
  disabled = false,
  onChange,
  size = 'sm',
  className,
  'data-testid': testId,
  trailing,
}) => {
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked
  }, [indeterminate, checked])

  return (
    <div className={cx('flex items-center gap-2', className)}>
      <input
        ref={ref}
        id={id}
        type="checkbox"
        className={cx('checkbox checkbox-primary', sizeClass[size])}
        checked={checked}
        disabled={disabled}
        aria-checked={indeterminate && !checked ? 'mixed' : checked}
        onChange={(e) => {
          if (!disabled) onChange(e.target.checked)
        }}
        data-testid={testId}
      />
      <label htmlFor={id} className={cx('label-text', disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}>
        {label}
      </label>
      {trailing}
    </div>
  )
}

export default Checkbox
